// src/components/accounts/AccountActions.js
// DROP-IN REPLACEMENT (UPDATED LAYOUT + CREATE MODAL WIRED)
//
// ✅ New Account opens CreateAccountPanel
// ✅ Create allowed for endUser/admin/owner
// ✅ Admin/Owner ONLY: Download Template + Upload Template
// ✅ Import logic preserved (UPSERT, safety skips, groupIds rules)
// ✅ Extra Excel columns stored under `extraFields`

import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  Download,
  Upload,
  Plus,
  X,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDoc,
  deleteField,
  writeBatch,
} from "firebase/firestore";
import * as XLSX from "xlsx";

// ✅ NEW: wire in CreateAccountPanel
import CreateAccountPanel from "./CreateAccountPanel";

const FALLBACK_STAFF_GROUP_ID = "grp_all_staff";

const uniqStrings = (arr) =>
  Array.from(
    new Set(
      (Array.isArray(arr) ? arr : [])
        .filter(Boolean)
        .map((v) => String(v).trim())
    )
  ).filter(Boolean);

const normalizeKey = (value) => {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_");
};

const normText = (v) => String(v ?? "").trim();
const normLower = (v) => normText(v).toLowerCase();
const norm5 = (z) => String(z || "").trim().slice(0, 5);
const normState = (s) => String(s || "").trim().toUpperCase();
const normalizeRole = (role) => {
  const r = normLower(role);
  if (["enduser", "end_user", "end-user", "end user", "user"].includes(r)) {
    return "enduser";
  }
  if (r === "owner" || r === "admin" || r === "customer") return r;
  return r;
};

const parseGroupIdsCell = (value) => {
  if (value === null || value === undefined) return { specified: false, ids: [] };
  if (Array.isArray(value)) return { specified: true, ids: uniqStrings(value) };

  const raw = String(value).trim();
  if (!raw) return { specified: false, ids: [] };

  const lowered = raw.toLowerCase();
  if (["clear", "none", "empty", "null"].includes(lowered)) return { specified: true, ids: [] };

  const parts = raw
    .split(/[,;|]/g)
    .map((s) => s.trim())
    .filter(Boolean);

  return { specified: true, ids: uniqStrings(parts) };
};

// Priority: accountNumber-like, else name
const getRowMatchKey = (row) => {
  const candidates = [
    row.accountNumber,
    row.account_no,
    row.accountNo,
    row.account_id,
    row.accountId,
    row.externalId,
    row.external_id,
    row.customerNumber,
    row.customer_number,
  ]
    .map((v) => normText(v))
    .filter(Boolean);

  const accNum = candidates[0] || "";
  if (accNum) return { type: "number", key: normLower(accNum) };

  const name = normText(row.name ?? row.Name ?? row.accountName ?? row["Account Name"]);
  if (name) return { type: "name", key: normLower(name) };

  return null;
};

const AccountActions = ({
  companyId,
  userProfile = null,
  visibleAccounts = [],
  accountColumns = [],
  columnVisibility = {},
  onColumnVisibilityChange,
  showColumnPicker = false,
  setShowColumnPicker,

  search = "",
  onSearchChange,

  filters,
  onFiltersChange,
  selectedAccountIds = [],
  onClearSelectedAccounts,

  canCreate = false, // kept for backwards compatibility
  canImport = false, // parent controls whether imports are enabled

  // optional legacy hook (if you already open a modal from parent)
  onCreateAccount,

  // refresh callback (used after import AND after create)
  onAfterImport,
}) => {
  const db = getFirestore();
  const auth = getAuth();
  const fileInputRef = useRef(null);
  const actionsWrapRef = useRef(null);
  const columnsWrapRef = useRef(null);

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  const [staffGroupId, setStaffGroupId] = useState(FALLBACK_STAFF_GROUP_ID);

  // ✅ NEW: create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(Boolean(String(search || "").trim()));

  // import state
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [bulkForm, setBulkForm] = useState({
    assignedToId: "",
    groupIds: [],
    overwriteGroups: false,
  });
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const desktopSearchInputRef = useRef(null);

  const myRole = normalizeRole(userProfile?.role);
  const isAdmin = myRole === "admin" || myRole === "owner";
  const isStaff = myRole === "admin" || myRole === "owner" || myRole === "enduser";
  const canBulkEdit = isAdmin;

  // ✅ Create: allow any staff role (endUser/admin/owner)
  const canCreateFinal = Boolean(companyId) && isStaff;

  // ✅ Import: admin/owner only + must be enabled by parent
  const canImportFinal = Boolean(companyId) && isAdmin && Boolean(canImport);
  const visibleAccountRows = Array.isArray(visibleAccounts) ? visibleAccounts : [];

  const getUserLabel = (u) => u.name || u.displayName || u.fullName || u.email || "Unnamed user";
  const getGroupLabel = (g) => g?.name || g?.title || g?.label || "Untitled group";

  useEffect(() => {
    if (!showActionsMenu) return undefined;

    const onPointerDown = (event) => {
      if (!actionsWrapRef.current?.contains(event.target)) {
        setShowActionsMenu(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowActionsMenu(false);
    };

    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showActionsMenu]);

  useEffect(() => {
    if (!showColumnPicker) return undefined;

    const onPointerDown = (event) => {
      if (!columnsWrapRef.current?.contains(event.target)) {
        setShowColumnPicker?.(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowColumnPicker?.(false);
    };

    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [setShowColumnPicker, showColumnPicker]);

  useEffect(() => {
    if (showDesktopSearch && desktopSearchInputRef.current) {
      desktopSearchInputRef.current.focus();
    }
  }, [showDesktopSearch]);

  // ---- Load company staffGroupId ----
  useEffect(() => {
    if (!companyId) return;

    const loadCompany = async () => {
      try {
        const snap = await getDoc(doc(db, "companies", companyId));
        const data = snap.exists() ? snap.data() : null;
        const sgid = data?.staffGroupId || FALLBACK_STAFF_GROUP_ID;
        setStaffGroupId(String(sgid));
      } catch {
        setStaffGroupId(FALLBACK_STAFF_GROUP_ID);
      }
    };

    loadCompany();
  }, [companyId, db]);

  // ---- Load territories ----
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      setTerritoriesLoading(true);
      try {
        const colRef = collection(db, "companies", companyId, "territories");
        const snap = await getDocs(colRef);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) =>
          String(a.name || "")
            .toLowerCase()
            .localeCompare(String(b.name || "").toLowerCase())
        );
        setTerritories(rows);
      } catch (e) {
        console.warn("[AccountActions] Failed to load territories:", e);
        setTerritories([]);
      } finally {
        setTerritoriesLoading(false);
      }
    };

    run();
  }, [companyId, db]);

  // ---- Load groups ----
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      setGroupsLoading(true);
      try {
        const colRef = collection(db, "companies", companyId, "groups");
        const snap = await getDocs(colRef);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => getGroupLabel(a).toLowerCase().localeCompare(getGroupLabel(b).toLowerCase()));
        setGroups(rows);
      } catch (e) {
        console.warn("[AccountActions] Failed to load groups:", e);
        setGroups([]);
      } finally {
        setGroupsLoading(false);
      }
    };

    run();
  }, [companyId, db]);

  // ---- Load users (admin: all staff; non-admin: just self) ----
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      setUsersLoading(true);
      try {
        const uid = auth.currentUser?.uid || null;
        if (!uid) {
          setUsers([]);
          return;
        }

        const qUsers = query(collection(db, "users"), where("companyId", "==", companyId));
        const snap = await getDocs(qUsers);
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => String(u.role || "").toLowerCase() !== "customer");

        rows.sort((a, b) => getUserLabel(a).toLowerCase().localeCompare(getUserLabel(b).toLowerCase()));
        if (isAdmin) {
          setUsers(rows);
          return;
        }

        const me = userProfile ? { id: uid, ...userProfile } : { id: uid };
        const scopedRows = rows.length
          ? rows
          : [me];
        setUsers(scopedRows);
      } catch (e) {
        console.warn("[AccountActions] Failed to load users:", e);
        const uid = auth.currentUser?.uid || null;
        const me = uid ? (userProfile ? { id: uid, ...userProfile } : { id: uid }) : null;
        setUsers(me ? [me] : []);
      } finally {
        setUsersLoading(false);
      }
    };

    run();
  }, [companyId, db, auth, isAdmin, userProfile]);

  const handleSearch = (v) => onSearchChange && onSearchChange(v);

  const setFilter = (field, value) => {
    onFiltersChange &&
      onFiltersChange((prev) => ({
        ...prev,
        [field]: value,
      }));
  };

  const clearFilters = () => {
    onFiltersChange &&
      onFiltersChange((prev) => ({
        ...prev,
        territoryId: "all",
        assignedToId: "all",
      }));
    onSearchChange && onSearchChange("");
  };

  const toggleBulkGroup = (groupId) => {
    const normalizedId = String(groupId || "").trim();
    if (!normalizedId) return;
    setBulkForm((prev) => {
      const set = new Set(uniqStrings(prev.groupIds));
      if (set.has(normalizedId)) set.delete(normalizedId);
      else set.add(normalizedId);
      return { ...prev, groupIds: Array.from(set) };
    });
  };

  const openBulkEdit = () => {
    if (!canBulkEdit || !selectedAccountIds.length) return;
    setBulkMessage("");
    setBulkError("");
    setBulkForm({
      assignedToId: "",
      groupIds: [],
      overwriteGroups: false,
    });
    setBulkEditOpen(true);
  };

  const closeBulkEdit = () => {
    if (bulkSaving) return;
    setBulkEditOpen(false);
    setBulkError("");
  };

  const runBulkUpdate = async () => {
    if (!companyId || !canBulkEdit || !selectedAccountIds.length) return;

    const cleanAssignedToId = String(bulkForm.assignedToId || "").trim();
    const shouldClearAssignee = cleanAssignedToId === "__clear__";
    const nextAssignedToId = shouldClearAssignee ? "" : cleanAssignedToId;
    const cleanGroupIds = uniqStrings(bulkForm.groupIds);

    if (!cleanAssignedToId && !bulkForm.overwriteGroups && cleanGroupIds.length === 0) {
      setBulkError("Choose an assignee, groups, or both before saving.");
      return;
    }

    setBulkSaving(true);
    setBulkError("");
    setBulkMessage("");

    try {
      const assignedUser = nextAssignedToId
        ? users.find((u) => String(u.id) === nextAssignedToId) || null
        : null;

      const accountIds = uniqStrings(selectedAccountIds);
      const accountSnaps = await Promise.all(
        accountIds.map((id) => getDoc(doc(db, "companies", companyId, "accounts", id)))
      );

      const existingAccounts = accountSnaps
        .filter((snap) => snap.exists())
        .map((snap) => ({ id: snap.id, ...snap.data() }));

      if (!existingAccounts.length) {
        throw new Error("None of the selected accounts could be loaded.");
      }

      for (let i = 0; i < existingAccounts.length; i += 400) {
        const batch = writeBatch(db);
        const slice = existingAccounts.slice(i, i + 400);

        slice.forEach((account) => {
          const accountRef = doc(db, "companies", companyId, "accounts", account.id);
          const nextGroupIds = bulkForm.overwriteGroups
            ? cleanGroupIds
            : uniqStrings([...(Array.isArray(account.groupIds) ? account.groupIds : []), ...cleanGroupIds]);

          const payload = {
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid || null,
          };

          if (cleanAssignedToId) {
            payload.assignedToId = nextAssignedToId || null;
            payload.assignedUserId = nextAssignedToId || null;
            payload.assignedToName = nextAssignedToId
              ? assignedUser
                ? getUserLabel(assignedUser)
                : account.assignedToName || ""
              : null;
          }

          if (bulkForm.overwriteGroups || cleanGroupIds.length > 0) {
            payload.groupIds = nextGroupIds;
          }

          batch.update(accountRef, payload);
        });

        await batch.commit();
      }

      setBulkMessage(
        `${existingAccounts.length} account${existingAccounts.length === 1 ? "" : "s"} updated successfully.`
      );
      setBulkEditOpen(false);
      onClearSelectedAccounts && onClearSelectedAccounts();
      onAfterImport && onAfterImport();
    } catch (err) {
      console.error("[AccountActions] Bulk update failed:", err);
      setBulkError(err?.message || "Bulk update failed.");
    } finally {
      setBulkSaving(false);
    }
  };

  const filtersActive =
    (filters?.territoryId && filters.territoryId !== "all") ||
    (filters?.assignedToId && filters.assignedToId !== "all") ||
    (search && String(search).trim().length > 0);
  const selectedCount = uniqStrings(selectedAccountIds).length;

  // ✅ New Account click → open your CreateAccountPanel
  const handleCreateClick = () => {
    if (!canCreateFinal) return;

    // If you already used parent-driven modal control, keep supporting it.
    if (typeof onCreateAccount === "function") {
      onCreateAccount();
      return;
    }

    setShowCreate(true);
  };

  // -----------------------------
  // Template download
  // -----------------------------
  const getTemplateRows = () => {
    return [
      {
        accountNumber: "ACCT-1001",
        name: "Seaside Grill",
        phone: "(555) 222-1001",
        email: "orders@seasidegrill.com",
        address1: "123 Ocean Ave",
        address2: "",
        city: "Miami",
        state: "FL",
        zip: "33101",
        country: "USA",
        territoryId: "",
        assignedToId: "",
        notes: "Preferred seafood samples",
      },
      {
        accountNumber: "ACCT-1002",
        name: "Downtown Deli",
        phone: "(555) 222-1002",
        email: "manager@downtowndeli.com",
        address1: "55 Market St",
        address2: "Suite 2",
        city: "Atlanta",
        state: "GA",
        zip: "30301",
        country: "USA",
        territoryId: "",
        assignedToId: "",
        notes: "",
      },
    ];
  };

  const downloadTemplate = () => {
    const rows = getTemplateRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AccountsTemplate");
    XLSX.writeFile(wb, "AccountsTemplate.xlsx");
  };

  const exportAccounts = async () => {
    try {
      setExporting(true);
      const rows = visibleAccountRows.map((account) => ({
        Name: account?.name || account?.accountName || "",
        "Account Number": account?.accountNumber || account?.accountNo || account?.externalId || "",
        Stage: account?.stage || "",
        "Assigned Rep": account?.assignedToName || "",
        "Contact Name": account?.contactName || account?.primaryContact?.name || "",
        "Contact Email": account?.contactEmail || account?.primaryContact?.email || account?.email || "",
        Phone: account?.phone || account?.locationPhone || "",
        Territory: account?.territoryName || "",
        City: account?.addressCity || account?.city || account?.address?.city || "",
        State: account?.addressState || account?.state || account?.address?.state || "",
        ZIP: account?.addressZip || account?.zip || account?.address?.zip || "",
        Active: account?.active === false ? "No" : "Yes",
      }));

      const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No visible accounts to export." }]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Accounts");
      XLSX.writeFile(wb, "AccountsExport.xlsx");
    } finally {
      setExporting(false);
    }
  };

  // -----------------------------
  // Import flow
  // -----------------------------
  const handleImportClick = () => {
    if (!canImportFinal) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const discoverColumns = (rows) => {
    const COL_SCAN_MAX = Math.min(rows.length, 25);
    const s = new Set();
    for (let i = 0; i < COL_SCAN_MAX; i++) {
      Object.keys(rows[i] || {}).forEach((k) => s.add(String(k)));
    }
    const rawCols = Array.from(s);
    const normalized = rawCols.map(normalizeKey);
    return { rawCols, normalizedSet: new Set(normalized) };
  };

  const buildPreview = async (rows) => {
    const colRef = collection(db, "companies", companyId, "accounts");
    const snap = await getDocs(colRef);

    const byNumber = {};
    const byName = {};
    const dupNumber = new Set();
    const dupName = new Set();

    snap.docs.forEach((d) => {
      const a = d.data() || {};
      const id = d.id;

      const accNumRaw =
        a.accountNumber || a.accountNo || a.account_no || a.externalId || a.customerNumber || "";
      const accNum = normLower(accNumRaw);
      if (accNum) {
        if (byNumber[accNum]) {
          dupNumber.add(accNum);
          delete byNumber[accNum];
        } else if (!dupNumber.has(accNum)) {
          byNumber[accNum] = { id, ...a };
        }
      }

      const nameRaw = a.name || a.accountName || "";
      const name = normLower(nameRaw);
      if (name) {
        if (byName[name]) {
          dupName.add(name);
          delete byName[name];
        } else if (!dupName.has(name)) {
          byName[name] = { id, ...a };
        }
      }
    });

    const { rawCols, normalizedSet } = discoverColumns(rows);
    const hasAny = (aliases) => aliases.some((a) => normalizedSet.has(normalizeKey(a)));

    const overwriteFields = [];
    if (hasAny(["accountNumber", "account_no", "accountNo", "customerNumber", "externalId"]))
      overwriteFields.push("accountNumber");
    if (hasAny(["name", "accountName", "account name"])) overwriteFields.push("name");
    if (hasAny(["phone", "phoneNumber"])) overwriteFields.push("phone");
    if (hasAny(["email"])) overwriteFields.push("email");
    if (hasAny(["website"])) overwriteFields.push("website");
    if (hasAny(["address1", "address_1", "address line1", "addressLine1"])) overwriteFields.push("address.line1");
    if (hasAny(["address2", "address_2", "address line2", "addressLine2"])) overwriteFields.push("address.line2");
    if (hasAny(["city"])) overwriteFields.push("address.city");
    if (hasAny(["state"])) overwriteFields.push("address.state");
    if (hasAny(["zip", "postal", "postalCode"])) overwriteFields.push("address.zip");
    if (hasAny(["country"])) overwriteFields.push("address.country");
    if (hasAny(["territoryId"])) overwriteFields.push("territoryId");
    if (hasAny(["assignedToId", "assignedUserId", "repUserId", "salesRepId"])) overwriteFields.push("assignedToId");
    if (hasAny(["notes"])) overwriteFields.push("notes");
    if (hasAny(["groupIds", "group_ids", "groups", "group ids"])) overwriteFields.push("groupIds (only if provided)");

    let willUpdate = 0;
    let willCreate = 0;
    let skipMissingKey = 0;
    let skipDupInDb = 0;
    let skipDupInFile = 0;

    const seen = new Set();

    rows.forEach((row) => {
      const mk = getRowMatchKey(row);
      if (!mk?.key) {
        skipMissingKey += 1;
        return;
      }

      const fileKey = `${mk.type}:${mk.key}`;
      if (seen.has(fileKey)) {
        skipDupInFile += 1;
        return;
      }
      seen.add(fileKey);

      if (mk.type === "number") {
        if (dupNumber.has(mk.key)) {
          skipDupInDb += 1;
          return;
        }
        if (byNumber[mk.key]) willUpdate += 1;
        else willCreate += 1;
      } else {
        if (dupName.has(mk.key)) {
          skipDupInDb += 1;
          return;
        }
        if (byName[mk.key]) willUpdate += 1;
        else willCreate += 1;
      }
    });

    return {
      rowsTotal: rows.length,
      columnsDetected: rawCols.length,
      overwriteFields,
      overwriteFieldCount: overwriteFields.length,
      willUpdate,
      willCreate,
      skipMissingKey,
      skipDupInDb,
      skipDupInFile,
      dupInDbNumberCount: dupNumber.size,
      dupInDbNameCount: dupName.size,
    };
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !companyId) return;

    setPreviewError("");
    setImporting(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });

        const p = await buildPreview(rows);

        setPendingRows(rows);
        setPreview(p);
        setShowImportConfirm(true);
      } catch (err) {
        console.error("[AccountImport] parse/preflight error:", err);
        setPreviewError("Failed to read this file. Please confirm it’s a valid .xlsx/.csv template.");
      } finally {
        setImporting(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const importAccounts = async (rows) => {
    const colRef = collection(db, "companies", companyId, "accounts");
    const snap = await getDocs(colRef);

    const byNumber = {};
    const byName = {};
    const dupNumber = new Set();
    const dupName = new Set();

    snap.docs.forEach((d) => {
      const a = d.data() || {};
      const id = d.id;

      const accNumRaw =
        a.accountNumber || a.accountNo || a.account_no || a.externalId || a.customerNumber || "";
      const accNum = normLower(accNumRaw);
      if (accNum) {
        if (byNumber[accNum]) {
          dupNumber.add(accNum);
          delete byNumber[accNum];
        } else if (!dupNumber.has(accNum)) {
          byNumber[accNum] = { id, ...a };
        }
      }

      const nameRaw = a.name || a.accountName || "";
      const name = normLower(nameRaw);
      if (name) {
        if (byName[name]) {
          dupName.add(name);
          delete byName[name];
        } else if (!dupName.has(name)) {
          byName[name] = { id, ...a };
        }
      }
    });

    const uid = auth.currentUser?.uid || null;
    const seen = new Set();

    const EXTRA_EXCLUDE = new Set([
      "accountnumber",
      "account_no",
      "accountno",
      "account_id",
      "accountid",
      "externalid",
      "external_id",
      "customernumber",
      "customer_number",
      "name",
      "accountname",
      "account_name",
      "phone",
      "phonenumber",
      "email",
      "website",
      "notes",
      "address1",
      "address_1",
      "addressline1",
      "address_line1",
      "address2",
      "address_2",
      "addressline2",
      "address_line2",
      "city",
      "state",
      "zip",
      "postal",
      "postalcode",
      "country",
      "territoryid",
      "assignedtoid",
      "assigneduserid",
      "repuserid",
      "salesrepid",
      "groupids",
      "groups",
      "group_ids",
      "group ids",
    ]);

    for (const row of rows) {
      const mk = getRowMatchKey(row);
      if (!mk?.key) {
        console.warn("[AccountImport] Skipping row (missing accountNumber or name):", row);
        continue;
      }

      const fileKey = `${mk.type}:${mk.key}`;
      if (seen.has(fileKey)) {
        console.warn("[AccountImport] Skipping duplicate row in file:", fileKey);
        continue;
      }
      seen.add(fileKey);

      if (mk.type === "number" && dupNumber.has(mk.key)) {
        console.warn("[AccountImport] Skipping accountNumber (duplicated in Firestore):", mk.key);
        continue;
      }
      if (mk.type === "name" && dupName.has(mk.key)) {
        console.warn("[AccountImport] Skipping name (duplicated in Firestore):", mk.key);
        continue;
      }

      const existing = mk.type === "number" ? byNumber[mk.key] || null : byName[mk.key] || null;

      // ---- groupIds logic ----
      const existingGroupIds = uniqStrings(existing?.groupIds);

      const groupCell =
        row.groupIds ?? row.groups ?? row.group_ids ?? row["group ids"] ?? row["Group Ids"] ?? row["Groups"] ?? undefined;

      const parsedGroup = parseGroupIdsCell(groupCell);

      let finalGroupIds;
      if (!existing) {
        finalGroupIds = [staffGroupId || FALLBACK_STAFF_GROUP_ID];
      } else {
        if (parsedGroup.specified) finalGroupIds = parsedGroup.ids;
        else if (existingGroupIds.length > 0) finalGroupIds = existingGroupIds;
        else finalGroupIds = [staffGroupId || FALLBACK_STAFF_GROUP_ID];
      }

      const name = normText(row.name ?? row.Name ?? row.accountName ?? row["Account Name"]) || null;

      const accountNumber =
        normText(
          row.accountNumber ??
            row.accountNo ??
            row.account_no ??
            row.customerNumber ??
            row.customer_number ??
            row.externalId ??
            row.external_id ??
            ""
        ) || null;

      const phone = normText(row.phone ?? row.phoneNumber ?? "") || null;
      const email = normText(row.email ?? "") || null;
      const website = normText(row.website ?? "") || null;
      const notes = normText(row.notes ?? "") || null;

      const address1 = normText(row.address1 ?? row.address_1 ?? row.addressLine1 ?? row["address line1"] ?? "") || "";
      const address2 = normText(row.address2 ?? row.address_2 ?? row.addressLine2 ?? row["address line2"] ?? "") || "";

      const city = normText(row.city ?? "") || "";
      const state = normState(row.state ?? "");
      const zip = norm5(row.zip ?? row.postal ?? row.postalCode ?? "");
      const country = normText(row.country ?? "USA") || "USA";

      const territoryId = normText(row.territoryId ?? "") || null;

      const assignedToId =
        normText(row.assignedToId ?? row.assignedUserId ?? row.repUserId ?? row.salesRepId ?? "") || null;

      // ---- extra columns passthrough -> extraFields ----
      const extraFields = {};
      Object.entries(row || {}).forEach(([k, v]) => {
        const nk = normalizeKey(k);
        if (!nk) return;
        if (EXTRA_EXCLUDE.has(nk) || EXTRA_EXCLUDE.has(String(k).toLowerCase().trim())) return;

        const val = typeof v === "string" ? v.trim() : v;
        if (val === "" || val === null || val === undefined) return;

        extraFields[nk] = val;
      });

      const mergedExtraFields = {
        ...(existing?.extraFields || {}),
        ...extraFields,
      };

      const payload = {
        name,
        accountNumber,
        phone,
        email,
        website,
        notes,

        address: {
          line1: address1 || null,
          line2: address2 || null,
          city: city || null,
          state: state || null,
          zip: zip || null,
          country: country || null,
        },

        city: city || null,
        state: state || null,
        zip: zip || null,

        territoryId,
        assignedToId,

        updatedAt: serverTimestamp(),
        updatedBy: uid || null,
        source: "excelImport",

        groupIds: finalGroupIds,

        extraFields: mergedExtraFields,
      };

      const updatePayload = {
        ...payload,
        visibility: deleteField(),
        hiddenFromEndUsers: deleteField(),
      };

      if (existing) {
        await updateDoc(doc(db, "companies", companyId, "accounts", existing.id), updatePayload);
      } else {
        await addDoc(colRef, {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: uid || null,
        });
      }
    }
  };

  // --- UI classes ---
  const outlineBtn =
    "inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-medium text-slate-700 whitespace-nowrap";
  const outlineBtnDisabled = "disabled:opacity-50 disabled:cursor-not-allowed";

  const selectBase =
    "h-10 px-3 rounded-xl border border-sky-200 bg-sky-50/80 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";

  const searchInput =
    "h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";

  return (
    <div className="w-full">
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} className="hidden" />

      {/* ✅ CREATE MODAL WIRED HERE */}
      <CreateAccountPanel
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={companyId}
        onCreated={async () => {
          // refresh list after create
          try {
            await onAfterImport?.();
            setBulkMessage("Location created successfully.");
          } finally {
            setShowCreate(false);
          }
        }}
      />

      {/* Toolbar */}
      <div className="w-full flex items-center gap-2 flex-wrap py-1 md:mt-2 overflow-visible">
        {/* Search */}
        <div className="min-w-0 flex-1 max-w-[420px]">
          <div className="md:hidden relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />
            <input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search accounts…"
              className={searchInput}
            />
          </div>

          <div className="hidden md:block relative">
            {showDesktopSearch ? (
              <div className="relative w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />
                <input
                  ref={desktopSearchInputRef}
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search accounts…"
                  className={searchInput}
                />
                <button
                  type="button"
                  onClick={() => {
                    handleSearch("");
                    setShowDesktopSearch(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Close account search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDesktopSearch(true)}
                className={outlineBtn}
                title="Search accounts"
              >
                <Search className="w-4 h-4 text-sky-500" />
                <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  Search
                </span>
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleCreateClick}
          disabled={!canCreateFinal}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-[13px] font-semibold whitespace-nowrap md:hidden"
          title={!canCreateFinal ? "You don’t have permission to create accounts" : "Create a new account"}
        >
          <Plus className="w-4 h-4" />
          New Account
        </button>

        {/* Filters */}
        <div className="inline-flex items-center gap-2 shrink-0">
          <select
            value={filters?.territoryId || "all"}
            onChange={(e) => setFilter("territoryId", e.target.value)}
            disabled={territoriesLoading}
            className={selectBase}
            title="Filter by territory"
          >
            <option value="all">All territories</option>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || "Untitled territory"}
              </option>
            ))}
          </select>

          <select
            value={filters?.assignedToId || "all"}
            onChange={(e) => setFilter("assignedToId", e.target.value)}
            disabled={usersLoading || !canBulkEdit}
            className={`${selectBase} disabled:opacity-60`}
            title={!canBulkEdit ? "You do not have permission to filter by user" : "Filter by assigned user"}
          >
            <option value="all">All assigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {getUserLabel(u)}
              </option>
            ))}
          </select>

          {filtersActive && (
            <button type="button" onClick={clearFilters} className={`${outlineBtn} ${outlineBtnDisabled}`} title="Clear">
              <X className="w-4 h-4 text-sky-500" />
              <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Clear
              </span>
            </button>
          )}
        </div>

        {(canImportFinal || canBulkEdit) ? (
        <div className="relative hidden shrink-0 z-30 md:block" ref={actionsWrapRef}>
            <button
              type="button"
              onClick={() => setShowActionsMenu((prev) => !prev)}
              className={`${outlineBtn} ${outlineBtnDisabled}`}
              disabled={importing}
              title="Account actions"
            >
              <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Actions
              </span>
              <ChevronDown className="w-4 h-4 text-sky-500" />
            </button>

            {showActionsMenu ? (
              <div className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                {canBulkEdit ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionsMenu(false);
                        openBulkEdit();
                      }}
                      disabled={selectedCount === 0}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <span>Bulk Edit</span>
                      <span className="text-[11px] text-slate-400">{selectedCount} selected</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowActionsMenu(false);
                        onClearSelectedAccounts && onClearSelectedAccounts();
                      }}
                      disabled={selectedCount === 0}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Clear Selection
                    </button>
                  </>
                ) : null}

                {canBulkEdit && canImportFinal ? <div className="h-px bg-slate-200" /> : null}

                <button
                  type="button"
                  onClick={async () => {
                    setShowActionsMenu(false);
                    await exportAccounts();
                  }}
                  disabled={exporting}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Download className="w-4 h-4 text-slate-500" />
                  {exporting ? "Exporting…" : "Export Accounts"}
                </button>

                {canImportFinal ? (
                  <>
                <div className="h-px bg-slate-200" />
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    downloadTemplate();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Download className="w-4 h-4 text-slate-500" />
                  Download Template
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    handleImportClick();
                  }}
                  disabled={importing}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4 text-slate-500" />
                  {importing ? "Reading…" : "Upload Template"}
                </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="relative hidden shrink-0 z-30 md:block" ref={columnsWrapRef}>
          <button
            type="button"
            onClick={() => setShowColumnPicker?.((prev) => !prev)}
            className={outlineBtn}
            title="Show/hide columns"
          >
            <SlidersHorizontal className="w-4 h-4 text-sky-500" />
            <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Columns
            </span>
          </button>

          {showColumnPicker ? (
            <div className="absolute left-0 top-full z-20 mt-2 w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
              <p className="mb-2 text-xs font-medium text-gray-500">Show / hide columns</p>
              <div className="space-y-2 text-xs">
                {(Array.isArray(accountColumns) ? accountColumns : [])
                  .filter((col) => col.id !== "select" && col.id !== "actions")
                  .map((col) => (
                    <label key={col.id} className="flex items-center gap-2 text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 text-blue-600 rounded border-gray-300"
                        checked={columnVisibility[col.id] !== false}
                        onChange={(e) =>
                          onColumnVisibilityChange?.((prev) => ({ ...prev, [col.id]: e.target.checked }))
                        }
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="hidden md:block whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em]">
          <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
            {visibleAccountRows.length} item{visibleAccountRows.length !== 1 ? "s" : ""}
          </span>
        </div>

        <button
          type="button"
          onClick={handleCreateClick}
          disabled={!canCreateFinal}
          className="ml-1 hidden md:inline-flex items-center gap-2 h-10 px-3.5 rounded-xl bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-500 text-[13px] font-semibold text-white whitespace-nowrap shadow-sm hover:from-sky-700 hover:via-blue-700 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
          title={!canCreateFinal ? "You don’t have permission to create accounts" : "Create a new account"}
        >
          <Plus className="w-4 h-4" />
          New Account
        </button>
      </div>

      {bulkMessage ? (
        <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {bulkMessage}
        </div>
      ) : null}

      {/* Import Confirm Modal */}
      {showImportConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Confirm Accounts Import</div>
                <div className="text-xs text-slate-500 mt-1">
                  Updates match by <strong>accountNumber</strong> first, otherwise <strong>name</strong>.
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingRows([]);
                  setPreview(null);
                  setPreviewError("");
                }}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              {previewError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-xs">
                  {previewError}
                </div>
              )}

              {!preview ? (
                <div className="text-xs text-slate-600">Building preview…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Rows in file</div>
                      <div className="text-lg font-semibold text-slate-900">{preview.rowsTotal}</div>
                    </div>
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Columns detected</div>
                      <div className="text-lg font-semibold text-slate-900">{preview.columnsDetected}</div>
                    </div>

                    <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3">
                      <div className="text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Will UPDATE
                      </div>
                      <div className="text-lg font-semibold text-emerald-900">{preview.willUpdate}</div>
                    </div>

                    <div className="border border-blue-200 bg-blue-50 rounded-xl p-3">
                      <div className="text-xs text-blue-700 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Will CREATE
                      </div>
                      <div className="text-lg font-semibold text-blue-900">{preview.willCreate}</div>
                    </div>
                  </div>

                  <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-xs text-amber-900">
                    <div className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Overwrite warning
                    </div>
                    <div className="mt-1">
                      This import includes <strong>{preview.overwriteFieldCount}</strong> column(s) that can overwrite
                      account fields.
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {(preview.overwriteFields || []).map((f) => (
                        <span
                          key={f}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-white/70 border border-amber-200 text-amber-900"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>

                  {(preview.skipMissingKey > 0 ||
                    preview.skipDupInDb > 0 ||
                    preview.skipDupInFile > 0 ||
                    preview.dupInDbNumberCount > 0 ||
                    preview.dupInDbNameCount > 0) && (
                    <div className="border border-slate-200 bg-slate-50 rounded-xl p-3 text-xs text-slate-700 space-y-1">
                      <div className="font-semibold text-slate-900">Safety skips</div>
                      {preview.skipMissingKey > 0 && (
                        <div>• {preview.skipMissingKey} row(s) skipped (missing accountNumber and name)</div>
                      )}
                      {preview.skipDupInFile > 0 && <div>• {preview.skipDupInFile} row(s) skipped (duplicate key in file)</div>}
                      {preview.skipDupInDb > 0 && <div>• {preview.skipDupInDb} row(s) skipped (key duplicated in Firestore)</div>}
                      {(preview.dupInDbNumberCount > 0 || preview.dupInDbNameCount > 0) && (
                        <div className="text-slate-500">Repair duplicates in Firestore to safely update those accounts by key.</div>
                      )}
                    </div>
                  )}

                  <div className="text-[11px] text-slate-500">
                    New accounts default to <strong>groupIds: [{staffGroupId}]</strong>. Existing accounts keep their
                    groupIds unless you include a <strong>groupIds</strong> column (or set it to “clear”).
                    <br />
                    Any extra Excel columns are saved under <strong>extraFields</strong>.
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingRows([]);
                  setPreview(null);
                  setPreviewError("");
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!preview || importing}
                onClick={async () => {
                  try {
                    setImporting(true);
                    await importAccounts(pendingRows);
                    setShowImportConfirm(false);
                    setPendingRows([]);
                    setPreview(null);
                    setPreviewError("");
                    onAfterImport && onAfterImport();
                  } catch (err) {
                    console.error("[AccountImport] Import error:", err);
                    setPreviewError("Import failed. Check console for details.");
                  } finally {
                    setImporting(false);
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {importing ? "Importing…" : "Yes, Import & Overwrite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Bulk Edit Accounts</div>
                <div className="text-xs text-slate-500 mt-1">
                  Update the assigned rep and group access for {selectedCount} selected account{selectedCount === 1 ? "" : "s"}.
                </div>
              </div>

              <button
                type="button"
                onClick={closeBulkEdit}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              {bulkError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-xs">
                  {bulkError}
                </div>
              ) : null}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">
                  Assign selected accounts to user
                </label>
                <select
                  value={bulkForm.assignedToId}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, assignedToId: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current assignee</option>
                  <option value="__clear__">Clear current assignee</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {getUserLabel(u)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="block text-xs font-semibold text-slate-700">
                    Group access
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={bulkForm.overwriteGroups}
                      onChange={(e) =>
                        setBulkForm((prev) => ({ ...prev, overwriteGroups: e.target.checked }))
                      }
                    />
                    Replace existing groups
                  </label>
                </div>

                {groupsLoading ? (
                  <div className="text-xs text-slate-500">Loading groups…</div>
                ) : groups.length === 0 ? (
                  <div className="text-xs text-slate-500">No groups found for this company.</div>
                ) : (
                  <div className="max-h-64 overflow-auto rounded-xl border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {groups.map((g) => {
                      const checked = uniqStrings(bulkForm.groupIds).includes(String(g.id));
                      return (
                        <label
                          key={g.id}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                            checked
                              ? "border-blue-300 bg-blue-50 text-blue-900"
                              : "border-slate-200 bg-white text-slate-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleBulkGroup(g.id)}
                          />
                          <span className="truncate">{getGroupLabel(g)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="mt-2 text-[11px] text-slate-500">
                  If "Replace existing groups" is off, selected groups will be added on top of each account’s current groups.
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={closeBulkEdit}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={bulkSaving}
                onClick={runBulkUpdate}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {bulkSaving ? "Saving…" : "Save Bulk Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountActions;
